function n(value) {
  return Number(value || 0) || 0;
}

function minutesSince(timestamp, now) {
  if (!timestamp) return null;
  const parsed = new Date(timestamp).getTime();
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.floor((now - parsed) / 60000));
}

function priorityLabel(count) {
  return `${count} high-priority open`;
}

function engineerStats(row, now) {
  const completedSet = new Set(row.completed || []);
  const parts = row.parts || [];
  const open = parts.filter((part) => !completedSet.has(part.serial));
  const openCalls = open.filter((part) => part.priority === "urgent").length;
  const openPriority = open.filter((part) => part.priority === "rep_req").length;
  const progress = parts.length ? (completedSet.size / parts.length) * 100 : 0;
  const highPriorityOpen = openCalls + openPriority;

  return {
    engineer: row.engineer || "Unknown",
    total: parts.length,
    done: completedSet.size,
    open: open.length,
    openCalls,
    openPriority,
    highPriorityOpen,
    progress,
    staleMinutes: minutesSince(row.lastUpdated, now),
  };
}

function rescueHelper(stats) {
  return [...stats]
    .filter(
      (row) =>
        row.total > 0 &&
        row.progress >= 80 &&
        row.highPriorityOpen === 0 &&
        row.open <= Math.max(1, Math.floor(row.total * 0.25)),
    )
    .sort((a, b) => b.progress - a.progress || a.open - b.open)[0];
}

export function buildLiveRescueBoard(progressRows, flags = [], options = {}) {
  const now = n(options.now) || Date.now();
  const maxActions = Math.max(1, n(options.maxActions || 4));
  const stats = (progressRows || []).map((row) => engineerStats(row, now));
  const helper = rescueHelper(stats);
  const actions = [];
  const openParts = stats.reduce((sum, row) => sum + row.open, 0);
  const openCalls = stats.reduce((sum, row) => sum + row.openCalls, 0);
  const openPriority = stats.reduce((sum, row) => sum + row.openPriority, 0);
  const stale = stats.filter((row) => row.staleMinutes !== null && row.staleMinutes >= 15);

  if ((flags || []).length > 0) {
    actions.push({
      id: "pending-grade-flags",
      tone: "risk",
      title: "Grade flags waiting",
      value: `${flags.length} pending`,
      detail: "Engineer-submitted grade changes can alter future earnings and reports.",
      action: "Review pending grade flags before trusting today's grade mix.",
    });
  }

  stale
    .sort((a, b) => b.staleMinutes - a.staleMinutes)
    .slice(0, 2)
    .forEach((row) => {
      actions.push({
        id: `stale-${row.engineer.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        tone: row.staleMinutes >= 30 ? "risk" : "warning",
        title: "Stale live link",
        value: `${row.staleMinutes}m old`,
        engineer: row.engineer,
        detail: `${row.engineer} has ${row.open} open parts and has not synced recently.`,
        action: "Chase the engineer or use Reset + link if their phone view is stuck.",
      });
    });

  stats
    .filter((row) => row.highPriorityOpen > 0 && row.progress < 80)
    .sort((a, b) => b.highPriorityOpen - a.highPriorityOpen || a.progress - b.progress)
    .slice(0, 2)
    .forEach((row) => {
      actions.push({
        id: `rescue-${row.engineer.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        tone: row.openCalls > 0 ? "risk" : "warning",
        title: row.openCalls > 0 ? "Open call rescue" : "Priority rescue",
        value: priorityLabel(row.highPriorityOpen),
        engineer: row.engineer,
        helper: helper?.engineer || null,
        detail: `${row.engineer} is ${row.done}/${row.total} complete with ${row.openCalls} calls and ${row.openPriority} priority parts still open.`,
        action: helper
          ? `Consider moving clean-up work to ${helper.engineer} or checking whether ${row.engineer} is blocked.`
          : "Check whether these open high-priority parts need chasing or reissuing.",
      });
    });

  if (!actions.length) {
    actions.push({
      id: "live-clear",
      tone: "good",
      title: "Live flow clear",
      value: "No rescue needed",
      detail: "No open calls, stale links, or pending grade flags are visible.",
      action: "Keep monitoring the normal engineer cards.",
    });
  }

  return {
    summary: {
      activeEngineers: stats.length,
      openParts,
      openCalls,
      openPriority,
      staleEngineers: stale.length,
      pendingFlags: (flags || []).length,
    },
    actions: actions.slice(0, maxActions),
    stats,
  };
}
